import React, { useState } from 'react';
import { Bell, Menu, Search, Settings, User, X, Type } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ThemeToggle } from "@/components/theme/ThemeToggle";
import { useTheme } from "@/components/theme/ThemeProvider";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useMobile } from "@/hooks/use-mobile";
import { useFontSize } from '@/contexts/FontSizeContext';
import { Slider } from "@/components/ui/slider";
import { toast } from 'sonner';

const Header = () => {
  const { resolvedTheme } = useTheme();
  const isMobile = useMobile();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const isDark = resolvedTheme === 'dark';
  const { fontSize, setFontSize } = useFontSize();
  const navigate = useNavigate();
  
  const handleLogout = () => {
    toast.success('Logged out successfully');
    navigate('/login', { replace: true });
  };
  
  const handleFontSizeChange = (values: number[]) => {
    setFontSize(values[0]);
  };
  
  return (
    <header className="w-full min-h-16 flex items-center justify-between border-b border-border bg-background/95 backdrop-blur-sm px-4 lg:px-8 sticky top-0 z-40 py-2">
      <div className="flex items-center">
        <Link to="/" className="flex items-center">
          <img 
            src="/lovable-uploads/ab5f9672-ee15-4519-bb8f-f1058082a152.png" 
            alt="ParticleMesh Logo" 
            className={`h-10 max-h-[85%] w-auto object-contain ${isDark ? 'filter invert' : ''}`}
          />
        </Link>
      </div>
      
      <div className="flex items-center space-x-2 sm:space-x-4">
        {!isMobile ? (
          <div className="relative hidden md:flex items-center">
            <Search className="absolute left-3 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search..."
              className="h-9 w-[200px] lg:w-[300px] rounded-full bg-secondary pl-9 pr-4 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        ) : (
          <>
            {isSearchOpen ? (
              <div className="absolute inset-0 bg-background z-50 flex items-center px-4">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="mr-2"
                  onClick={() => setIsSearchOpen(false)}
                >
                  <X className="h-5 w-5" />
                </Button>
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search..."
                    autoFocus
                    className="h-9 w-full rounded-full bg-secondary pl-9 pr-4 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>
            ) : (
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-muted-foreground md:hidden"
                onClick={() => setIsSearchOpen(true)}
              >
                <Search className="h-5 w-5" />
              </Button>
            )}
          </>
        )}
        
        <Button variant="ghost" size="icon" className="text-muted-foreground">
          <Bell className="h-5 w-5" />
        </Button>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="text-muted-foreground">
              <Settings className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64 z-50 p-4">
            <DropdownMenuLabel>Settings</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/security" className="overflow-hidden text-ellipsis whitespace-nowrap">Security</Link>
            </DropdownMenuItem>
            <DropdownMenuItem>
              <div className="flex items-center justify-between w-full">
                <span className="overflow-hidden text-ellipsis whitespace-nowrap">Theme</span>
                <ThemeToggle />
              </div>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <div className="px-2 py-2">
              <div className="flex items-center mb-2">
                <Type className="h-4 w-4 mr-2 text-muted-foreground flex-shrink-0" />
                <span className="text-sm">Font Size: {fontSize}px</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs flex-shrink-0">12</span>
                <Slider 
                  value={[fontSize]} 
                  min={12} 
                  max={25} 
                  step={1} 
                  onValueChange={handleFontSizeChange} 
                  className="flex-1"
                />
                <span className="text-xs flex-shrink-0">25</span>
              </div>
              <div className="font-control-preview mt-2 text-center" style={{ fontSize: `${fontSize}px` }}>
                Preview
              </div>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/settings" className="overflow-hidden text-ellipsis whitespace-nowrap">More Settings</Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        
        {isMobile && (
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="p-0">
              <MobileSidebar />
            </SheetContent>
          </Sheet>
        )}
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-full">
              <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground">
                <User className="h-5 w-5" />
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 z-50">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/profile" className="overflow-hidden text-ellipsis whitespace-nowrap">Profile</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/settings" className="overflow-hidden text-ellipsis whitespace-nowrap">Settings</Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={handleLogout}
              className="overflow-hidden text-ellipsis whitespace-nowrap"
            >
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

const MobileSidebar = () => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const location = { pathname: window.location.pathname };
  
  const navItems = [
    { name: 'Command Center', href: '/', icon: () => <span className="i-lucide-layout-dashboard"></span> },
    { name: 'Devices', href: '/devices', icon: () => <span className="i-lucide-wifi"></span> },
    { name: 'Network Health', href: '/network-health', icon: () => <span className="i-lucide-bar-chart-3"></span> },
    { name: 'Users', href: '/users', icon: () => <span className="i-lucide-users"></span> },
    { name: 'Add Device', href: '/add-device', icon: () => <span className="i-lucide-plus"></span> },
    { name: 'Security', href: '/security', icon: () => <span className="i-lucide-shield"></span> },
    { name: 'Alerts', href: '/alerts', icon: () => <span className="i-lucide-alert-triangle"></span> },
  ];
  
  return (
    <div className={`h-full flex flex-col p-6 ${isDark ? 'bg-sidebar' : 'bg-secondary'}`}>
      <div className="flex-1 mt-6">
        <nav className="space-y-2">
          {navItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center px-3 py-3 text-sm font-medium rounded-md transition-all ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground hover:bg-primary/10"
                }`}
              >
                <span className="mr-3 h-5 w-5 flex items-center justify-center">
                  {item.icon()}
                </span>
                <span>{item.name}</span>
              </Link>
            );
          })}
        </nav>
      </div>
      
      <div className="mt-auto pt-4 border-t border-border">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Dark Mode</span>
          <ThemeToggle />
        </div>
      </div>
    </div>
  );
};

export default Header;
